// Project edit drawer — extracted from ProjectsPage so it can be reused on
// the Project 360 page (/projects/{id}) for in-place editing (WI-11901).
//
// Owns: the entire "Edit project / New project" right-side panel, including
// the Details, Phases, and Pricing tabs and all of their sub-controls.
//
// Usage: render <ProjectDrawer ... /> conditionally; pass `project={null}`
// for a create flow and `project={existingProject}` for edit. `onSaved` fires
// after a successful create or update — the caller is responsible for
// invalidating their own React Query caches in addition to the drawer's.

import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Pencil, Plus, Layers, X, Coins, Tag, Users, ChevronDown, ChevronRight,
  Search, Receipt,
} from 'lucide-react'
import { api } from '@/lib/api/client'
import type {
  Project, FortnoxCustomer, Phase, PhaseStatus, Activity, PhaseResource,
  ActivityResource, PersonDirectoryEntry, InvoiceDetailLevel, User,
  ProjectRevenueCategory,
} from '@/types/api'
import { INVOICE_DETAIL_LEVELS, PROJECT_REVENUE_CATEGORIES } from '@/types/api'
import RatesTable from '@/components/rates/RatesTable'

interface DrawerProps {
  project:   Project | null
  customers: FortnoxCustomer[]
  onClose:   () => void
  onSaved:   () => void
}

export function ProjectDrawer({ project, customers, onClose, onSaved }: DrawerProps) {
  const isEdit = project !== null
  const queryClient = useQueryClient()

  const [tab,                  setTab]                  = useState<'details' | 'phases' | 'pricing'>('details')
  const [name,                 setName]                 = useState(project?.name ?? '')
  const [customerId,           setCustomerId]           = useState<number | null>(project?.customerId ?? null)
  const [description,          setDescription]          = useState(project?.description ?? '')
  const [customerQ,            setCustomerQ]            = useState(project?.customerName ?? '')
  const [customerOpen,         setCustomerOpen]         = useState(false)
  const [detailOverride,       setDetailOverride]       = useState<InvoiceDetailLevel | ''>(project?.invoiceDetailLevelOverride ?? '')
  const [projectManagerId,     setProjectManagerId]     = useState<number | null>(project?.projectManagerId ?? null)
  const [revenueCategory,      setRevenueCategory]      = useState<ProjectRevenueCategory>(project?.revenueCategory ?? 'CONSULTING')
  const [isServiceAgreement,   setIsServiceAgreement]   = useState<boolean>(project?.isServiceAgreement ?? false)
  const [isBillable,           setIsBillable]           = useState<boolean>(project?.isBillable ?? true)
  const customerRef = useRef<HTMLDivElement>(null)

  const { data: phases = [] } = useQuery({
    queryKey: ['projects', project?.id, 'phases'],
    queryFn:  () => api.get<Phase[]>(`/projects/${project?.id}/phases`),
    enabled:  isEdit,
  })

  // For the Project Manager picker (WI-11888). /users is Admin-only, which is fine —
  // only Admins can edit projects.
  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn:  () => api.get<User[]>('/users'),
  })

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (customerRef.current && !customerRef.current.contains(e.target as Node))
        setCustomerOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(customerQ.toLowerCase())
  )

  const selectCustomer = (c: FortnoxCustomer | null) => {
    setCustomerId(c?.id ?? null)
    setCustomerQ(c?.name ?? '')
    setCustomerOpen(false)
  }

  const createMutation = useMutation({
    mutationFn: (body: { name: string; customerId: number | null; description: string; projectManagerId: number | null }) =>
      api.post<{ id: number }>('/projects', body),
  })

  const updateMutation = useMutation({
    mutationFn: (body: {
      name: string; customerId: number | null; description: string;
      status: string; invoiceDetailLevelOverride: InvoiceDetailLevel | null;
      projectManagerId: number | null;
      revenueCategory: ProjectRevenueCategory;
      isServiceAgreement: boolean;
      isBillable: boolean;
    }) => api.put(`/projects/${project?.id}`, body),
  })

  const handleSave = async () => {
    if (!name.trim()) return
    try {
      if (isEdit) {
        await updateMutation.mutateAsync({
          name: name.trim(), customerId, description: description.trim(),
          status: project?.status ?? '',
          invoiceDetailLevelOverride: detailOverride || null,
          projectManagerId,
          revenueCategory,
          isServiceAgreement,
          isBillable,
        })
      } else {
        await createMutation.mutateAsync({
          name: name.trim(), customerId, description: description.trim(),
          projectManagerId,
        })
      }
      onSaved()
    } catch {
      // errors surfaced by React Query
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <>
      <div
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 40,
        }}
        onClick={onClose}
      />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: isEdit && (tab === 'pricing' || tab === 'phases') ? 760 : 420, maxWidth: '100vw',
        background: 'var(--ns-surface)', borderLeft: '1px solid var(--ns-border)',
        zIndex: 50, display: 'flex', flexDirection: 'column',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.18)',
      }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--ns-border)' }}>
          <h2 style={{ fontSize: 16, fontWeight: 600 }}>{isEdit ? 'Edit project' : 'New project'}</h2>
          {isEdit && (
            <div style={{ display: 'flex', gap: 4, marginTop: 12 }}>
              {(['details', 'phases', 'pricing'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`chip${tab === t ? ' active' : ''}`}
                  style={{ textTransform: 'capitalize' }}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>

         {(!isEdit || tab === 'details') && <>

          <div>
            <label style={{ fontSize: 12.5, fontWeight: 500, display: 'block', marginBottom: 6 }}>
              Project name <span style={{ color: 'var(--ns-err)' }}>*</span>
            </label>
            <input
              className="input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. LKAB Dashboard"
              style={{ width: '100%' }}
              autoFocus
            />
          </div>

          <div>
            <label style={{ fontSize: 12.5, fontWeight: 500, display: 'block', marginBottom: 6 }}>
              Customer
            </label>
            <div ref={customerRef} style={{ position: 'relative' }}>
              <div style={{ position: 'relative' }}>
                <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ns-text-muted)', pointerEvents: 'none' }} />
                <input
                  className="input"
                  value={customerQ}
                  onChange={e => { setCustomerQ(e.target.value); setCustomerOpen(true); if (!e.target.value) setCustomerId(null) }}
                  onFocus={() => setCustomerOpen(true)}
                  placeholder="Internal (no customer)"
                  style={{ width: '100%', paddingLeft: 30 }}
                />
              </div>
              {customerOpen && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                  background: 'var(--ns-surface)', border: '1px solid var(--ns-border)',
                  borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  maxHeight: 220, overflowY: 'auto', marginTop: 2,
                }}>
                  <div
                    onMouseDown={() => selectCustomer(null)}
                    style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', color: 'var(--ns-text-muted)', fontStyle: 'italic' }}
                    className="dropdown-item"
                  >
                    Internal (no customer)
                  </div>
                  {filteredCustomers.map(c => (
                    <div
                      key={c.id}
                      onMouseDown={() => selectCustomer(c)}
                      style={{
                        padding: '8px 12px', fontSize: 13, cursor: 'pointer',
                        background: c.id === customerId ? 'var(--ns-brand-soft)' : undefined,
                      }}
                      className="dropdown-item"
                    >
                      {c.name}
                    </div>
                  ))}
                  {filteredCustomers.length === 0 && (
                    <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--ns-text-muted)' }}>No matches</div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12.5, fontWeight: 500, display: 'block', marginBottom: 6 }}>
              Description
            </label>
            <textarea
              className="input"
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
              style={{ width: '100%', resize: 'vertical' }}
              placeholder="Optional notes about this project"
            />
          </div>

          <div>
            <label style={{ fontSize: 12.5, fontWeight: 500, display: 'block', marginBottom: 6 }}>
              Project manager
            </label>
            <select
              className="input"
              style={{ width: '100%', fontSize: 13 }}
              value={projectManagerId ?? ''}
              onChange={e => setProjectManagerId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">— none —</option>
              {users
                .filter(u => u.isActive)
                .map(u => <option key={u.id} value={u.id}>{u.displayName}</option>)}
            </select>
          </div>

          <div>
            <label style={{ fontSize: 12.5, fontWeight: 500, display: 'block', marginBottom: 6 }}>
              <Receipt size={12} style={{ display: 'inline', marginRight: 4 }} />
              Invoice detail level override
            </label>
            <p className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
              Overrides the customer default for this project. Leave blank to use the customer setting.
            </p>
            <select
              className="input"
              style={{ width: '100%', fontSize: 13 }}
              value={detailOverride}
              onChange={e => setDetailOverride(e.target.value as InvoiceDetailLevel | '')}
            >
              <option value="">— Use customer default —</option>
              {INVOICE_DETAIL_LEVELS.map(l => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </div>

          {isEdit && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 8, borderTop: '1px solid var(--ns-border)' }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', color: 'var(--ns-text-muted)', textTransform: 'uppercase' }}>
              Revenue
            </div>

            <div>
              <label style={{ fontSize: 12.5, fontWeight: 500, display: 'block', marginBottom: 6 }}>
                Default revenue category
              </label>
              <p className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                Used at invoice generation when no phase or activity overrides it. Service Agreement flag and offshore resources take precedence.
              </p>
              <select
                className="input"
                style={{ width: '100%', fontSize: 13 }}
                value={revenueCategory}
                onChange={e => setRevenueCategory(e.target.value as ProjectRevenueCategory)}
              >
                {PROJECT_REVENUE_CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <label className="row" style={{ gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isServiceAgreement}
                onChange={e => setIsServiceAgreement(e.target.checked)}
              />
              <span>
                <strong>Service Agreement project</strong>
                <span className="muted" style={{ display: 'block', fontSize: 11.5 }}>
                  All time on this project resolves to SERVICE_AGREEMENT revenue (overrides category and offshore).
                </span>
              </span>
            </label>

            <label className="row" style={{ gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isBillable}
                onChange={e => setIsBillable(e.target.checked)}
              />
              <span>
                <strong>Billable</strong>
                <span className="muted" style={{ display: 'block', fontSize: 11.5 }}>
                  When off, time on this project is tracked for utilization but excluded from invoice generation.
                </span>
              </span>
            </label>
          </div>
          )}

         </>}

         {isEdit && project && tab === 'phases' && (
           <ProjectPhasesTab projectId={project.id} phases={phases} />
         )}

         {isEdit && project && tab === 'pricing' && (
           <ProjectPricingTab projectId={project.id} phases={phases} />
         )}

        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--ns-border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-brand"
            onClick={handleSave}
            disabled={!name.trim() || isPending}
          >
            {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create project'}
          </button>
        </div>
      </div>
    </>
  )
}

// ─── Project pricing tab ──────────────────────────────────────────────────────
// Three sections: project-level rate, per-phase rate, per-activity rate. Each
// section renders a RatesTable scoped to its level. The chip rows let the user
// select which phase or activity to manage.

function ProjectPricingTab({ projectId, phases }: { projectId: number; phases: Phase[] }) {
  const [selectedPhaseId, setSelectedPhaseId] = useState<number | null>(null)
  const [selectedActivityId, setSelectedActivityId] = useState<number | null>(null)
  const activePhases = phases.filter(ph => ph.isActive)

  const { data: activities = [] } = useQuery({
    queryKey: ['phases', selectedPhaseId, 'activities'],
    queryFn:  () => api.get<Activity[]>(`/phases/${selectedPhaseId}/activities`),
    enabled:  selectedPhaseId != null,
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      <section>
        <div className="row" style={{ gap: 6, marginBottom: 6 }}>
          <Coins size={13} style={{ color: 'var(--ns-brand-strong)' }} />
          <label style={{ fontSize: 12.5, fontWeight: 500 }}>Project rate</label>
        </div>
        <p className="muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 8 }}>
          Overrides the workspace default for this project. Phase or activity rates win when set.
        </p>
        <RatesTable scope="project" parentId={projectId} />
      </section>

      <section>
        <div className="row" style={{ gap: 6, marginBottom: 6 }}>
          <Layers size={13} style={{ color: 'var(--ns-brand-strong)' }} />
          <label style={{ fontSize: 12.5, fontWeight: 500 }}>Phase rates</label>
        </div>
        <p className="muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 8 }}>
          Per-phase rates override the project rate for that phase only.
        </p>
        <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
          {activePhases.map(ph => (
            <button
              key={ph.id}
              className={`chip${selectedPhaseId === ph.id ? ' active' : ''}`}
              onClick={() => {
                setSelectedPhaseId(selectedPhaseId === ph.id ? null : ph.id)
                setSelectedActivityId(null)
              }}
            >
              {ph.name}
              {ph.isDefault && <span className="muted" style={{ marginLeft: 4 }}>(default)</span>}
            </button>
          ))}
        </div>
        {selectedPhaseId != null ? (
          <RatesTable scope="phase" parentId={selectedPhaseId} />
        ) : (
          <div className="muted" style={{ fontSize: 12, padding: '8px 0' }}>
            Select a phase to manage its pricing.
          </div>
        )}
      </section>

      {selectedPhaseId != null && (
        <section>
          <div className="row" style={{ gap: 6, marginBottom: 6 }}>
            <Tag size={13} style={{ color: 'var(--ns-brand-strong)' }} />
            <label style={{ fontSize: 12.5, fontWeight: 500 }}>Activity rates</label>
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 8 }}>
            Most-specific override: applies to a particular activity within the selected phase.
          </p>
          <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
            {activities.filter(a => a.isActive).map(a => (
              <button
                key={a.id}
                className={`chip${selectedActivityId === a.id ? ' active' : ''}`}
                onClick={() => setSelectedActivityId(selectedActivityId === a.id ? null : a.id)}
              >
                {a.name}
                {a.isDefault && <span className="muted" style={{ marginLeft: 4 }}>(default)</span>}
              </button>
            ))}
          </div>
          {selectedActivityId != null ? (
            <RatesTable scope="activity" parentId={selectedActivityId} />
          ) : (
            <div className="muted" style={{ fontSize: 12, padding: '8px 0' }}>
              Select an activity to manage its pricing.
            </div>
          )}
        </section>
      )}

    </div>
  )
}

// ─── Project phases tab (WI-11753) ────────────────────────────────────────────
// Each phase expands to reveal its priority, status, activities, and resources.
// Default phase / default activity cannot be deactivated.

function ProjectPhasesTab({ projectId, phases }: { projectId: number; phases: Phase[] }) {
  const queryClient = useQueryClient()
  const [newPhaseName, setNewPhaseName] = useState('')
  const [openPhaseId,  setOpenPhaseId]  = useState<number | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'phases'] })

  const addPhase = useMutation({
    mutationFn: (name: string) => api.post(`/projects/${projectId}/phases`, { name }),
    onSuccess:  () => { invalidate(); setNewPhaseName('') },
  })

  const deactivatePhase = useMutation({
    mutationFn: (phaseId: number) => api.delete(`/projects/${projectId}/phases/${phaseId}`),
    onSuccess:  () => invalidate(),
  })

  const updatePhase = useMutation({
    mutationFn: ({ phaseId, body }: {
      phaseId: number
      body: { name: string; priority: number; status: string }
    }) => api.put(`/projects/${projectId}/phases/${phaseId}`, body),
    onSuccess: () => invalidate(),
  })

  const activePhases = phases.filter(ph => ph.isActive)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p className="muted" style={{ fontSize: 12, margin: 0 }}>
        Phases split a project into billing stages. Each phase can have its own priority,
        status, activities, and assigned resources.
      </p>

      {activePhases.map(ph => (
        <PhaseRow
          key={ph.id}
          phase={ph}
          open={openPhaseId === ph.id}
          onToggle={() => setOpenPhaseId(openPhaseId === ph.id ? null : ph.id)}
          onSave={body => updatePhase.mutate({ phaseId: ph.id, body })}
          onDeactivate={() => {
            if (confirm(`Deactivate phase "${ph.name}"?`)) deactivatePhase.mutate(ph.id)
          }}
        />
      ))}

      <div style={{ display: 'flex', gap: 6 }}>
        <input
          className="input"
          value={newPhaseName}
          onChange={e => setNewPhaseName(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && newPhaseName.trim()) addPhase.mutate(newPhaseName.trim())
          }}
          placeholder="New phase name…"
          style={{ flex: 1 }}
        />
        <button
          className="btn btn-ghost"
          onClick={() => { if (newPhaseName.trim()) addPhase.mutate(newPhaseName.trim()) }}
          disabled={!newPhaseName.trim() || addPhase.isPending}
        >
          <Plus size={13} />Add phase
        </button>
      </div>
    </div>
  )
}

function PhaseRow({ phase, open, onToggle, onSave, onDeactivate }: {
  phase:        Phase
  open:         boolean
  onToggle:     () => void
  onSave:       (body: { name: string; priority: number; status: string }) => void
  onDeactivate: () => void
}) {
  const [name,     setName]     = useState(phase.name)
  const [priority, setPriority] = useState(phase.priority)
  const [status,   setStatus]   = useState(phase.status)
  const dirty =
    name !== phase.name || priority !== phase.priority || status !== phase.status

  return (
    <div style={{
      border: '1px solid var(--ns-border)', borderRadius: 6,
      background: 'var(--ns-surface)', overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px', cursor: 'pointer',
        background: open ? 'var(--ns-surface-2)' : undefined,
      }} onClick={onToggle}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Layers size={13} style={{ color: 'var(--ns-text-muted)' }} />
        <span style={{ fontWeight: 500, fontSize: 13 }}>{phase.name}</span>
        {phase.isDefault && (
          <span className="badge" style={{ fontSize: 10 }}>default</span>
        )}
        <span className="badge" style={{
          fontSize: 10,
          background: phase.status === 'closed' ? 'var(--ns-warn-soft)' : 'var(--ns-ok-soft)',
          color:      phase.status === 'closed' ? 'var(--ns-warn)' : 'var(--ns-ok)',
        }}>{phase.status}</span>
        <span className="muted" style={{ fontSize: 11 }}>P{phase.priority}</span>
        <div style={{ flex: 1 }} />
        {!phase.isDefault && (
          <button
            className="btn btn-ghost btn-icon"
            title="Deactivate phase"
            style={{ color: 'var(--ns-err)' }}
            onClick={e => { e.stopPropagation(); onDeactivate() }}
          >
            <X size={12} />
          </button>
        )}
      </div>

      {open && (
        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 14, borderTop: '1px solid var(--ns-border)' }}>
          {/* Phase metadata editor */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11.5, fontWeight: 500, display: 'block', marginBottom: 4 }}>Name</label>
              <input
                className="input"
                value={name}
                onChange={e => setName(e.target.value)}
                style={{ width: '100%', fontSize: 12.5 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 11.5, fontWeight: 500, display: 'block', marginBottom: 4 }}>Priority</label>
              <select
                className="input"
                value={priority}
                onChange={e => setPriority(Number(e.target.value))}
                style={{ fontSize: 12.5 }}
              >
                {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>P{n}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11.5, fontWeight: 500, display: 'block', marginBottom: 4 }}>Status</label>
              <select
                className="input"
                value={status}
                onChange={e => setStatus(e.target.value as PhaseStatus)}
                style={{ fontSize: 12.5 }}
              >
                <option value="active">active</option>
                <option value="closed">closed</option>
              </select>
            </div>
            <button
              className="btn btn-brand"
              disabled={!dirty || !name.trim()}
              onClick={() => onSave({ name: name.trim(), priority, status })}
              style={{ fontSize: 12.5 }}
            >Save</button>
          </div>

          <PhaseResourcesPanel phaseId={phase.id} />
          <PhaseActivitiesPanel phaseId={phase.id} />
        </div>
      )}
    </div>
  )
}

// ─── Phase resources panel ────────────────────────────────────────────────────

function PhaseResourcesPanel({ phaseId }: { phaseId: number }) {
  const queryClient = useQueryClient()
  const qkey = ['phases', phaseId, 'resources']

  const { data: resources = [] } = useQuery({
    queryKey: qkey,
    queryFn:  () => api.get<PhaseResource[]>(`/phases/${phaseId}/resources`),
  })

  const { data: people = [] } = useQuery({
    queryKey: ['people'],
    queryFn:  () => api.get<PersonDirectoryEntry[]>('/people'),
    staleTime: 5 * 60 * 1000,
  })

  const [adding, setAdding] = useState(false)
  const [personId, setPersonId] = useState<string>('')
  const [budgetHours, setBudgetHours] = useState<string>('')

  const invalidate = () => queryClient.invalidateQueries({ queryKey: qkey })

  const addResource = useMutation({
    mutationFn: (body: { personId: number; budgetHours: number | null }) =>
      api.post(`/phases/${phaseId}/resources`, body),
    onSuccess: () => { invalidate(); setAdding(false); setPersonId(''); setBudgetHours('') },
  })

  const updateBudget = useMutation({
    mutationFn: ({ id, budgetHours }: { id: number; budgetHours: number | null }) =>
      api.put(`/phase-resources/${id}`, { budgetHours }),
    onSuccess: () => invalidate(),
  })

  const removeResource = useMutation({
    mutationFn: (id: number) => api.delete(`/phase-resources/${id}`),
    onSuccess:  () => invalidate(),
  })

  const assignedIds = new Set(resources.map(r => r.personId))
  const eligible = people.filter(p => p.isActive && !assignedIds.has(p.id))

  return (
    <section>
      <div className="row" style={{ gap: 6, marginBottom: 6 }}>
        <Users size={13} style={{ color: 'var(--ns-brand-strong)' }} />
        <label style={{ fontSize: 12.5, fontWeight: 500 }}>Resources</label>
        <span className="muted" style={{ fontSize: 11.5 }}>
          — people assignable to this phase's activities
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
        {resources.map(r => (
          <div key={r.id} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 10px', borderRadius: 6, background: 'var(--ns-surface-2)',
            fontSize: 12.5,
          }}>
            <span style={{ flex: 1 }}>{r.personName}</span>
            <BudgetHoursInput
              value={r.budgetHours}
              onChange={v => updateBudget.mutate({ id: r.id, budgetHours: v })}
            />
            <button
              className="btn btn-ghost btn-icon"
              title="Remove resource"
              style={{ color: 'var(--ns-err)' }}
              onClick={() => {
                if (confirm(`Remove ${r.personName} from this phase?`)) removeResource.mutate(r.id)
              }}
            >
              <X size={11} />
            </button>
          </div>
        ))}
        {resources.length === 0 && !adding && (
          <div className="muted" style={{ fontSize: 12, padding: '4px 0' }}>No resources assigned.</div>
        )}
      </div>

      {adding ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <select
            className="input"
            value={personId}
            onChange={e => setPersonId(e.target.value)}
            style={{ flex: 1, fontSize: 12.5 }}
          >
            <option value="">Select person…</option>
            {eligible.map(p => (
              <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
            ))}
          </select>
          <input
            className="input mono"
            type="number"
            min="0"
            step="1"
            placeholder="Budget hrs"
            value={budgetHours}
            onChange={e => setBudgetHours(e.target.value)}
            style={{ width: 110, fontSize: 12.5 }}
          />
          <button
            className="btn btn-brand"
            disabled={!personId || addResource.isPending}
            onClick={() => addResource.mutate({
              personId:    Number(personId),
              budgetHours: budgetHours ? Number(budgetHours) : null,
            })}
            style={{ fontSize: 12.5 }}
          >Add</button>
          <button
            className="btn btn-ghost"
            onClick={() => { setAdding(false); setPersonId(''); setBudgetHours('') }}
            style={{ fontSize: 12.5 }}
          >Cancel</button>
        </div>
      ) : (
        <button
          className="btn btn-ghost"
          onClick={() => setAdding(true)}
          style={{ gap: 4, fontSize: 12.5 }}
          disabled={eligible.length === 0}
        >
          <Plus size={12} />Add resource
        </button>
      )}
    </section>
  )
}

function BudgetHoursInput({ value, onChange }: {
  value:    number | null
  onChange: (v: number | null) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft,   setDraft]   = useState('')

  if (!editing) {
    return (
      <button
        className="btn btn-ghost btn-sm"
        style={{ fontSize: 11.5, padding: '2px 8px' }}
        onClick={() => { setDraft(value != null ? String(value) : ''); setEditing(true) }}
      >
        {value != null ? `${value.toLocaleString('sv-SE')} h budget` : <span className="muted">Set budget…</span>}
      </button>
    )
  }
  return (
    <input
      autoFocus
      type="number"
      className="input mono"
      style={{ width: 90, fontSize: 11.5 }}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => {
        const n = parseFloat(draft)
        onChange(isNaN(n) ? null : n)
        setEditing(false)
      }}
      onKeyDown={e => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        if (e.key === 'Escape') setEditing(false)
      }}
      placeholder="hrs"
    />
  )
}

// ─── Phase activities panel ───────────────────────────────────────────────────
// Lists activities for the phase. Each activity row can be expanded to manage
// its own resource assignments (drawn from the phase's resource pool).

function PhaseActivitiesPanel({ phaseId }: { phaseId: number }) {
  const queryClient = useQueryClient()
  const qkey = ['phases', phaseId, 'activities']

  const { data: activities = [] } = useQuery({
    queryKey: qkey,
    queryFn:  () => api.get<Activity[]>(`/phases/${phaseId}/activities`),
  })

  const [openId,   setOpenId]   = useState<number | null>(null)
  const [adding,   setAdding]   = useState(false)
  const [newName,  setNewName]  = useState('')
  const [newPrio,  setNewPrio]  = useState(3)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: qkey })

  const addActivity = useMutation({
    mutationFn: (body: { name: string; priority: number }) =>
      api.post(`/phases/${phaseId}/activities`, body),
    onSuccess: () => { invalidate(); setAdding(false); setNewName(''); setNewPrio(3) },
  })

  const updateActivity = useMutation({
    mutationFn: ({ id, body }: { id: number; body: { name: string; priority: number } }) =>
      api.put(`/activities/${id}`, body),
    onSuccess: () => invalidate(),
  })

  const deactivateActivity = useMutation({
    mutationFn: (id: number) => api.delete(`/activities/${id}`),
    onSuccess:  () => invalidate(),
  })

  const visible = activities.filter(a => a.isActive)

  return (
    <section>
      <div className="row" style={{ gap: 6, marginBottom: 6 }}>
        <Tag size={13} style={{ color: 'var(--ns-brand-strong)' }} />
        <label style={{ fontSize: 12.5, fontWeight: 500 }}>Activities</label>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
        {visible.map(a => (
          <ActivityRow
            key={a.id}
            phaseId={phaseId}
            activity={a}
            open={openId === a.id}
            onToggle={() => setOpenId(openId === a.id ? null : a.id)}
            onSave={body => updateActivity.mutate({ id: a.id, body })}
            onDeactivate={() => {
              if (confirm(`Deactivate activity "${a.name}"?`)) deactivateActivity.mutate(a.id)
            }}
          />
        ))}
        {visible.length === 0 && !adding && (
          <div className="muted" style={{ fontSize: 12, padding: '4px 0' }}>
            No activities yet.
          </div>
        )}
      </div>

      {adding ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            className="input"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Activity name…"
            style={{ flex: 1, fontSize: 12.5 }}
            autoFocus
          />
          <select
            className="input"
            value={newPrio}
            onChange={e => setNewPrio(Number(e.target.value))}
            style={{ width: 80, fontSize: 12.5 }}
          >
            {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>P{n}</option>)}
          </select>
          <button
            className="btn btn-brand"
            disabled={!newName.trim() || addActivity.isPending}
            onClick={() => addActivity.mutate({ name: newName.trim(), priority: newPrio })}
            style={{ fontSize: 12.5 }}
          >Add</button>
          <button className="btn btn-ghost" onClick={() => setAdding(false)} style={{ fontSize: 12.5 }}>Cancel</button>
        </div>
      ) : (
        <button
          className="btn btn-ghost"
          onClick={() => setAdding(true)}
          style={{ gap: 4, fontSize: 12.5 }}
        >
          <Plus size={12} />Add activity
        </button>
      )}
    </section>
  )
}

function ActivityRow({ phaseId, activity, open, onToggle, onSave, onDeactivate }: {
  phaseId:      number
  activity:     Activity
  open:         boolean
  onToggle:     () => void
  onSave:       (body: { name: string; priority: number }) => void
  onDeactivate: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(activity.name)
  const [priority, setPriority] = useState(activity.priority)

  return (
    <div style={{
      border: '1px solid var(--ns-border)', borderRadius: 6,
      background: 'var(--ns-surface-2)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
        cursor: 'pointer',
      }} onClick={onToggle}>
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {editing ? (
          <>
            <input
              className="input"
              value={name}
              onChange={e => setName(e.target.value)}
              onClick={e => e.stopPropagation()}
              style={{ flex: 1, fontSize: 12.5 }}
              autoFocus
            />
            <select
              className="input"
              value={priority}
              onChange={e => setPriority(Number(e.target.value))}
              onClick={e => e.stopPropagation()}
              style={{ width: 70, fontSize: 12.5 }}
            >
              {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>P{n}</option>)}
            </select>
            <button
              className="btn btn-brand btn-sm"
              onClick={e => { e.stopPropagation(); onSave({ name: name.trim(), priority }); setEditing(false) }}
              disabled={!name.trim()}
              style={{ fontSize: 11.5 }}
            >Save</button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={e => { e.stopPropagation(); setEditing(false); setName(activity.name); setPriority(activity.priority) }}
              style={{ fontSize: 11.5 }}
            >Cancel</button>
          </>
        ) : (
          <>
            <span style={{ flex: 1, fontSize: 12.5 }}>{activity.name}</span>
            {activity.isDefault && (
              <span className="badge" style={{ fontSize: 10 }}>default</span>
            )}
            <span className="muted" style={{ fontSize: 11 }}>P{activity.priority}</span>
            <button
              className="btn btn-ghost btn-icon"
              title="Edit"
              onClick={e => { e.stopPropagation(); setEditing(true) }}
            >
              <Pencil size={11} />
            </button>
            {!activity.isDefault && (
              <button
                className="btn btn-ghost btn-icon"
                title="Deactivate"
                style={{ color: 'var(--ns-err)' }}
                onClick={e => { e.stopPropagation(); onDeactivate() }}
              >
                <X size={11} />
              </button>
            )}
          </>
        )}
      </div>
      {open && (
        <div style={{ padding: '8px 12px', borderTop: '1px solid var(--ns-border)' }}>
          <ActivityResourcesPanel phaseId={phaseId} activityId={activity.id} />
        </div>
      )}
    </div>
  )
}

// ─── Activity resources panel ─────────────────────────────────────────────────
// People here must already be on the parent phase (backend enforces this).

function ActivityResourcesPanel({ phaseId, activityId }: { phaseId: number; activityId: number }) {
  const queryClient = useQueryClient()
  const qkey = ['activities', activityId, 'resources']

  const { data: assigned = [] } = useQuery({
    queryKey: qkey,
    queryFn:  () => api.get<ActivityResource[]>(`/activities/${activityId}/resources`),
  })

  const { data: phaseResources = [] } = useQuery({
    queryKey: ['phases', phaseId, 'resources'],
    queryFn:  () => api.get<PhaseResource[]>(`/phases/${phaseId}/resources`),
  })

  const [adding, setAdding] = useState(false)
  const [personId, setPersonId] = useState('')

  const invalidate = () => queryClient.invalidateQueries({ queryKey: qkey })

  const addResource = useMutation({
    mutationFn: (pid: number) => api.post(`/activities/${activityId}/resources`, { personId: pid }),
    onSuccess: () => { invalidate(); setAdding(false); setPersonId('') },
  })

  const removeResource = useMutation({
    mutationFn: (id: number) => api.delete(`/activity-resources/${id}`),
    onSuccess:  () => invalidate(),
  })

  const assignedIds = new Set(assigned.map(r => r.personId))
  const eligible = phaseResources.filter(p => !assignedIds.has(p.personId))

  return (
    <div>
      <div className="row" style={{ gap: 6, marginBottom: 6 }}>
        <Users size={11} style={{ color: 'var(--ns-text-muted)' }} />
        <span className="muted" style={{ fontSize: 11.5, fontWeight: 500 }}>
          Resources on this activity
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
        {assigned.map(r => (
          <span key={r.id} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '3px 8px', borderRadius: 12, fontSize: 11.5,
            background: 'var(--ns-surface)', border: '1px solid var(--ns-border)',
          }}>
            {r.personName}
            <button
              onClick={() => removeResource.mutate(r.id)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--ns-text-muted)' }}
              title="Remove from activity"
            >
              <X size={10} />
            </button>
          </span>
        ))}
        {assigned.length === 0 && !adding && (
          <span className="muted" style={{ fontSize: 11.5 }}>No resources on this activity.</span>
        )}
      </div>

      {adding ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <select
            className="input"
            value={personId}
            onChange={e => setPersonId(e.target.value)}
            style={{ flex: 1, fontSize: 12 }}
          >
            <option value="">Select phase resource…</option>
            {eligible.map(p => (
              <option key={p.id} value={p.personId}>{p.personName}</option>
            ))}
          </select>
          <button
            className="btn btn-brand btn-sm"
            disabled={!personId || addResource.isPending}
            onClick={() => addResource.mutate(Number(personId))}
            style={{ fontSize: 12 }}
          >Add</button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => { setAdding(false); setPersonId('') }}
            style={{ fontSize: 12 }}
          >Cancel</button>
        </div>
      ) : (
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => setAdding(true)}
          disabled={eligible.length === 0}
          title={eligible.length === 0 ? 'All phase resources are already assigned' : ''}
          style={{ gap: 4, fontSize: 11.5 }}
        >
          <Plus size={11} />Add resource
        </button>
      )}
    </div>
  )
}
